import type { Day, Kind, Slot } from "../types";
import { DAYS, laneLayout } from "../planner";
import { esc, fmt } from "../text";
import { measure, type FontKey } from "./metrics";
import { ellipsize, wrap } from "./wrap";
import { THEMES, blockColors, type Theme, type ThemeId } from "./themes";
import { FONT_BY_KEY, fontFaceCss, fontStack, type FontFile } from "./fonts";
import { mix, normalize } from "./color";

export type Paper = "a4-landscape" | "a4-portrait" | "a5-landscape" | "phone";
export type Lang = "bs" | "en";
export interface LegendEntry { title: string; color: string; lines: string[] }

export interface RenderOptions {
  theme: ThemeId;
  paper: Paper;
  title?: string;
  subtitle?: string;
  showRoom: boolean;
  showTeacher: boolean;
  showGroup: boolean;
  showWeeks: boolean;
  clock: 24 | 12;
  legend?: LegendEntry[];
  clashIds?: Set<string>;
  lang: Lang;
  footer?: string;
  /** Font sources for @font-face: file name (e.g. "Newsreader-Regular") → data: URI or URL, or a resolver. Omit to reference fonts by family name only. */
  fonts?: Record<string, string> | ((f: FontFile) => string | undefined);
}

export const PAPERS: Record<Paper, { w: number; h: number; mm: [number, number] | null }> = {
  "a4-landscape": { w: 1123, h: 794, mm: [297, 210] },
  "a4-portrait": { w: 794, h: 1123, mm: [210, 297] },
  "a5-landscape": { w: 794, h: 559, mm: [210, 148] },
  phone: { w: 1170, h: 2532, mm: null },
};

const STR = {
  bs: {
    days: ["Ponedjeljak", "Utorak", "Srijeda", "Četvrtak", "Petak", "Subota", "Nedjelja"],
    short: ["Pon", "Uto", "Sri", "Čet", "Pet", "Sub", "Ned"],
    kinds: { P: "predavanja", AV: "auditorne vježbe", LV: "laboratorijske vježbe", S: "seminar", T: "tutorijal", other: "ostalo" } as Record<Kind, string>,
    title: "Moj raspored", brand: "Slušam", tag: "nezvanični planer · Univerzitet u Sarajevu",
    sessions: "termina", session: "termin", hours: "sati sedmično", nDays: "dana", nDay: "dan",
    clash: "preklapanje", edited: "izmijenjen termin", free: "slobodno", courses: "Predmeti", empty: "Još nema odabranih predmeta",
    odd: "neparne sedm.", even: "parne sedm.", wk: "sedm.", gr: "gr.", more: (n: number) => `+ još ${n}`,
  },
  en: {
    days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    short: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    kinds: { P: "lecture", AV: "exercises", LV: "lab", S: "seminar", T: "tutorial", other: "other" } as Record<Kind, string>,
    title: "My timetable", brand: "Slušam", tag: "unofficial planner · University of Sarajevo",
    sessions: "sessions", session: "session", hours: "hours a week", nDays: "days", nDay: "day",
    clash: "clash", edited: "edited", free: "free", courses: "Courses", empty: "No courses picked yet",
    odd: "odd weeks", even: "even weeks", wk: "wk", gr: "gr.", more: (n: number) => `+ ${n} more`,
  },
};
type Str = (typeof STR)["bs"];

const KIND_ORDER: Kind[] = ["P", "AV", "LV", "S", "T"];

const n = (x: number) => (Number.isFinite(x) ? Math.round(x * 100) / 100 : 0);
type AttrVal = string | number | undefined | null | false;
const attrs = (a: Record<string, AttrVal>) =>
  Object.entries(a).filter(([, v]) => v !== undefined && v !== null && v !== false && v !== "")
    .map(([k, v]) => ` ${k}="${typeof v === "number" ? n(v) : esc(v)}"`).join("");
const rect = (x: number, y: number, w: number, h: number, a: Record<string, AttrVal> = {}) =>
  `<rect${attrs({ x, y, width: Math.max(0, w), height: Math.max(0, h), ...a })}/>`;
const line = (x1: number, y1: number, x2: number, y2: number, a: Record<string, AttrVal>) => `<line${attrs({ x1, y1, x2, y2, ...a })}/>`;
const path = (d: string, a: Record<string, AttrVal>) => `<path${attrs({ d, ...a })}/>`;
const P = (...pts: number[]) => pts.map(n).join(" ");

interface Ctx { t: Theme; o: RenderOptions; L: Str; used: Set<FontKey>; s: number; W: number; H: number; body: string[] }

function txt(R: Ctx, x: number, y: number, s: string, f: FontKey, size: number, fill: string, o: { anchor?: "middle" | "end"; track?: number; opacity?: number } = {}): string {
  if (!s) return "";
  R.used.add(f);
  const F = FONT_BY_KEY[f];
  if (o.anchor) x -= measure(s, f, size, o.track ?? 0) / (o.anchor === "middle" ? 2 : 1);
  return `<text${attrs({
    x, y, "font-family": fontStack(f), "font-size": size, "font-weight": F.weight === 600 ? 600 : undefined,
    "font-style": F.style === "italic" ? "italic" : undefined, fill, "letter-spacing": o.track ? n(o.track) : undefined, "fill-opacity": o.opacity,
  })}>${esc(s)}</text>`;
}

const upper = (s: string) => s.toLocaleUpperCase("bs");

function clock(m: number, c: 24 | 12): string {
  if (c === 24) return fmt(m);
  const h = Math.floor(m / 60) % 24, mm = String(m % 60).padStart(2, "0");
  return `${h % 12 || 12}:${mm} ${h < 12 ? "AM" : "PM"}`;
}
function range(a: number, b: number, c: 24 | 12): string {
  if (c === 24) return `${fmt(a)}–${fmt(b)}`;
  const A = clock(a, 12), B = clock(b, 12);
  return A.slice(-2) === B.slice(-2) ? `${A.slice(0, -3)}–${B}` : `${A}–${B}`;
}
const hourLabel = (h: number, c: 24 | 12) => (c === 24 ? `${String(h).padStart(2, "0")}:00` : `${h % 12 || 12} ${h < 12 || h === 24 ? "AM" : "PM"}`);
const num = (x: number, lang: Lang) => { const s = (Math.round(x * 10) / 10).toString(); return lang === "bs" ? s.replace(".", ",") : s; };

export function prettyWeeks(w: string | null | undefined, L: Str): string {
  if (!w) return "";
  const s = w.toLowerCase();
  if (/^\s*(every|svake?|sve)\b/.test(s)) return "";
  if (/\b(odd|neparn)/.test(s)) return L.odd;
  if (/\b(even|parn)/.test(s)) return L.even;
  const body = w.replace(/\(.*?\)/g, " ").replace(/wk\s*1\s*=.*$/i, " ").replace(/[a-zA-Zčćšđž.]+/gi, " ").replace(/\s*[-–]\s*/g, "–").replace(/\s*,\s*/g, ",").trim();
  return body && /\d/.test(body) ? `${L.wk} ${body.replace(/\s+/g, ",")}` : "";
}

const kindLabel = (k: Slot["kind"]) => (KIND_ORDER.includes(k as Kind) ? k : "");

function details(R: Ctx, sl: Slot): { room: string; teacher: string; extra: string } {
  const { o, L } = R;
  const grp = o.showGroup && sl.group && sl.group !== "-" ? (/^\d+$/.test(sl.group) ? `${L.gr} ${sl.group}` : sl.group) : "";
  const wk = o.showWeeks ? prettyWeeks(sl.weeks, L) : "";
  return { room: o.showRoom && sl.room ? sl.room : "", teacher: o.showTeacher && sl.lecturer ? sl.lecturer : "", extra: [grp, wk].filter(Boolean).join(" · ") };
}

function hatch(x: number, y: number, w: number, h: number, step: number): string {
  const d: string[] = [];
  const x1 = x + w, y1 = y + h;
  for (let k = x + y + step; k < x1 + y1; k += step) {
    const ax = Math.max(x, k - y1), bx = Math.min(x1, k - y);
    if (bx - ax < 0.5) continue;
    d.push(`M${n(ax)} ${n(k - ax)}L${n(bx)} ${n(k - bx)}`);
  }
  return d.join("");
}

function badge(R: Ctx, x: number, baseline: number, label: string, size: number, c: { badgeBg: string; badgeFg: string; badgeStroke: string | null }): { svg: string; w: number } {
  if (!label) return { svg: "", w: 0 };
  const padX = size * 0.38, h = size * 1.42, w = measure(label, "monoBold", size) + padX * 2;
  const y = baseline - size * 0.74 - (h - size * 0.74) / 2;
  const r = R.t.block.badge === "soft" ? h / 2 : Math.min(2.5 * R.s, h / 4);
  return {
    svg: rect(x, y, w, h, { rx: r, fill: c.badgeStroke ? "none" : c.badgeBg, stroke: c.badgeStroke, "stroke-width": c.badgeStroke ? 0.8 * R.s : undefined }) +
      txt(R, x + w / 2, baseline, label, "monoBold", size, c.badgeFg, { anchor: "middle" }),
    w,
  };
}

const dogEar = (x: number, y: number, sz: number, fill: string) => path(`M${P(x - sz, y)}L${P(x, y)}L${P(x, y + sz)}Z`, { fill });

/* ---------------------------------------------------------------- header / footer / legend */

function stats(slots: Slot[]) {
  const hours = slots.reduce((a, s) => a + (s.end - s.start) / 60, 0);
  const days = new Set(slots.map((s) => s.day)).size;
  return { count: slots.length, hours, days };
}

function header(R: Ctx, slots: Slot[], x: number, y: number, w: number): number {
  const { t, o, L, s } = R;
  const out: string[] = [];
  const st = stats(slots);
  const items = [
    { v: String(st.count), l: st.count === 1 ? L.session : L.sessions },
    { v: num(st.hours, o.lang), l: L.hours },
    { v: String(st.days), l: st.days === 1 ? L.nDay : L.nDays },
  ];
  const statSize = 24 * s, labSize = 7 * s, gap = 18 * s;
  let sx = x + w, statsW = 0;
  const statSvg: string[] = [];
  for (const it of [...items].reverse()) {
    const lw = measure(upper(it.l), "monoBold", labSize, 0.9 * s), vw = measure(it.v, "display", statSize);
    const iw = Math.max(lw, vw);
    statSvg.push(txt(R, sx, y + 30 * s, it.v, "display", statSize, t.ink, { anchor: "end" }));
    statSvg.push(txt(R, sx, y + 41 * s, upper(it.l), "monoBold", labSize, t.ink3, { anchor: "end", track: 0.9 * s }));
    sx -= iw + gap; statsW += iw + gap;
  }
  out.push(txt(R, x, y + 8 * s, upper(`${L.brand} · ${o.lang === "bs" ? "UNSA" : "UNSA"}`), t.fonts.kicker, 7.5 * s, t.ink3, { track: 1.4 * s }));
  const titleSize = 31 * s * t.titleScale;
  const tw = w - statsW - 10 * s;
  const title = ellipsize(o.title || L.title, t.fonts.title, titleSize, tw, t.fonts.title === "bodyBold" ? -0.02 * titleSize : 0);
  out.push(txt(R, x, y + 10 * s + titleSize * 0.92, title, t.fonts.title, titleSize, t.ink, { track: t.fonts.title === "bodyBold" ? -0.02 * titleSize : undefined }));
  let bottom = y + 10 * s + titleSize * 0.92;
  if (o.subtitle) {
    const sub = ellipsize(o.subtitle, "body", 10.5 * s, tw);
    out.push(txt(R, x, bottom + 15 * s, sub, "body", 10.5 * s, t.ink2));
    bottom += 15 * s;
  }
  out.push(...statSvg);
  bottom = Math.max(bottom, y + 41 * s) + 9 * s;
  switch (t.headerRule) {
    case "double": out.push(line(x, bottom, x + w, bottom, { stroke: t.ink, "stroke-width": 1.4 * s }), line(x, bottom + 3 * s, x + w, bottom + 3 * s, { stroke: t.ink, "stroke-width": 0.6 * s })); bottom += 3 * s; break;
    case "thick": out.push(rect(x, bottom - 1.5 * s, w, 3 * s, { fill: t.ink })); bottom += 1.5 * s; break;
    case "single": out.push(line(x, bottom, x + w, bottom, { stroke: t.rule, "stroke-width": 1 * s })); break;
    default: bottom -= 4 * s;
  }
  R.body.push(...out);
  return bottom;
}

function footerKeys(R: Ctx, slots: Slot[]): { w: number; draw: (x: number, baseline: number) => string } {
  const { t, L, o, s } = R;
  const parts: { w: number; draw: (x: number, b: number) => string }[] = [];
  const size = 7.5 * s, bs = 6.5 * s, gap = 12 * s;
  const neutral = { badgeBg: t.ink2, badgeFg: t.dark ? t.paper : "#ffffff", badgeStroke: t.block.badge === "outline" ? t.ink : null };
  if (t.block.badge === "outline") neutral.badgeBg = "none";
  if (t.block.badge === "outline") neutral.badgeFg = t.ink;
  for (const k of KIND_ORDER) {
    if (!slots.some((sl) => sl.kind === k)) continue;
    const label = L.kinds[k];
    const bw = measure(k, "monoBold", bs) + bs * 0.76;
    const w = bw + 4 * s + measure(label, "mono", size);
    parts.push({ w, draw: (x, b) => badge(R, x, b, k, bs, neutral).svg + txt(R, x + bw + 4 * s, b, label, "mono", size, t.ink3) });
  }
  const sw = 13 * s, sh = 9 * s;
  if (o.clashIds && slots.some((sl) => o.clashIds!.has(sl.id))) {
    const w = sw + 4 * s + measure(L.clash, "mono", size);
    parts.push({ w, draw: (x, b) => rect(x, b - sh + 1 * s, sw, sh, { fill: t.surface, stroke: t.hot, "stroke-width": 1 * s }) +
      path(hatch(x, b - sh + 1 * s, sw, sh, 3 * s), { stroke: t.hot, "stroke-width": 0.9 * s, "stroke-opacity": 0.7 }) + txt(R, x + sw + 4 * s, b, L.clash, "mono", size, t.ink3) });
  }
  if (slots.some((sl) => sl.edited)) {
    const w = sw + 4 * s + measure(L.edited, "mono", size);
    parts.push({ w, draw: (x, b) => rect(x, b - sh + 1 * s, sw, sh, { fill: t.surface, stroke: t.rule, "stroke-width": 0.8 * s }) +
      dogEar(x + sw, b - sh + 1 * s, 5 * s, t.ink2) + txt(R, x + sw + 4 * s, b, L.edited, "mono", size, t.ink3) });
  }
  const w = parts.reduce((a, p) => a + p.w, 0) + Math.max(0, parts.length - 1) * gap;
  return { w, draw: (x, b) => { let cx = x; return parts.map((p) => { const sv = p.draw(cx, b); cx += p.w + gap; return sv; }).join(""); } };
}

function footer(R: Ctx, slots: Slot[], x: number, w: number, baseline: number): number {
  const { t, o, L, s } = R;
  const keys = footerKeys(R, slots);
  const left = o.footer || `${L.brand} · ${L.tag}`;
  const size = 7.5 * s;
  const lw = measure(left, "mono", size);
  if (lw + keys.w + 24 * s <= w) {
    R.body.push(txt(R, x, baseline, left, "mono", size, t.ink3), keys.draw(x + w - keys.w, baseline));
    return baseline - 12 * s;
  }
  R.body.push(txt(R, x, baseline, ellipsize(left, "mono", size, w), "mono", size, t.ink3));
  R.body.push(keys.draw(x, baseline - 14 * s));
  return baseline - 26 * s;
}

interface LegendLayout { h: number; draw: (x: number, y: number) => string }

function legendLayout(R: Ctx, entries: LegendEntry[], w: number, maxH: number): LegendLayout | null {
  const { t, L, s } = R;
  if (!entries.length) return null;
  const minCol = 190 * s, gapX = 18 * s;
  const maxCols = Math.max(1, Math.min(entries.length, Math.floor((w + gapX) / (minCol + gapX))));
  const cols = Math.ceil(entries.length / Math.ceil(entries.length / maxCols));
  const colW = (w - gapX * (cols - 1)) / cols;
  const ts = 9.5 * s, ms = 7.5 * s, head = 16 * s, sw = 8 * s;
  for (const [maxLines, titleLines] of [[3, 2], [2, 2], [1, 1], [0, 1]] as const) {
    for (let count = entries.length; count >= Math.min(entries.length, cols); count -= cols) {
      const shown = entries.slice(0, count);
      const more = entries.length - count;
      const cells = shown.map((e) => {
        const tl = wrap(e.title, "bodyBold", ts, colW - sw - 6 * s, titleLines);
        const ls = e.lines.filter(Boolean).slice(0, maxLines).map((l) => ellipsize(l, "mono", ms, colW - sw - 6 * s));
        return { e, tl, ls, h: tl.length * ts * 1.2 + ls.length * ms * 1.4 + 11 * s };
      });
      const rows: (typeof cells)[] = [];
      for (let i = 0; i < cells.length; i += cols) rows.push(cells.slice(i, i + cols));
      const rowH = rows.map((r) => Math.max(...r.map((c) => c.h)));
      const h = head + rowH.reduce((a, b) => a + b, 0) + (more ? ms * 1.6 : 0);
      if (h > maxH && !(maxLines === 0 && count <= cols)) continue;
      if (h > maxH) return null;
      return {
        h,
        draw: (x, y) => {
          const out = [txt(R, x, y + 7 * s, upper(L.courses), t.fonts.kicker, 7 * s, t.ink3, { track: 1.2 * s })];
          let cy = y + head;
          rows.forEach((row, ri) => {
            row.forEach((c, ci) => {
              const cx = x + ci * (colW + gapX);
              const col = t.color(normalize(c.e.color, t.ink));
              out.push(t.id === "mono" ? rect(cx + 0.5 * s, cy + 0.5 * s, sw - s, sw - s, { fill: "none", stroke: col, "stroke-width": s })
                : rect(cx, cy, sw, sw, { rx: t.block.radius > 4 ? sw / 2 : 1.5 * s, fill: col }));
              let ly = cy + ts * 0.82;
              for (const l of c.tl) { out.push(txt(R, cx + sw + 6 * s, ly, l, "bodyBold", ts, t.ink)); ly += ts * 1.2; }
              ly += ms * 0.15;
              for (const l of c.ls) { out.push(txt(R, cx + sw + 6 * s, ly + ms * 0.2, l, "mono", ms, t.ink3)); ly += ms * 1.4; }
            });
            cy += rowH[ri];
          });
          if (more) out.push(txt(R, x, cy + ms, L.more(more), "mono", ms, t.ink3));
          return out.join("");
        },
      };
    }
  }
  return null;
}

/* ---------------------------------------------------------------- week grid */

function visibleDays(slots: Slot[]): Day[] {
  return DAYS.filter((d, i) => i < 5 || slots.some((s) => s.day === d));
}

function grid(R: Ctx, slots: Slot[], x: number, y: number, w: number, h: number) {
  const { t, o, L, s } = R;
  const out: string[] = [];
  const days = visibleDays(slots);
  const startH = Math.max(0, Math.min(8, ...slots.map((sl) => Math.floor(sl.start / 60))));
  const endH = Math.min(24, Math.max(18, ...slots.map((sl) => Math.ceil(sl.end / 60))));
  const hourFont = t.fonts.hour, hs = 7.5 * s;
  const gutter = measure(hourLabel(o.clock === 12 ? 11 : 10, o.clock), hourFont, hs) + 12 * s;
  const headH = (t.dayUpper ? 22 : 26) * s;
  const colW = (w - gutter) / days.length;
  const top = y + headH, hourH = (h - headH) / (endH - startH);
  const yOf = (m: number) => top + ((m - startH * 60) / 60) * hourH;
  const r = t.frameRadius * s;

  if (t.frame) out.push(rect(x, y, w, h, { rx: r, fill: t.surface, stroke: t.ink === "#000000" ? t.ink : t.dark ? t.rule : t.ink, "stroke-width": t.frame * s }));
  else if (t.surface !== t.paper) out.push(rect(x, y, w, h, { rx: r, fill: t.surface }));
  if (t.zebra) days.forEach((_, i) => { if (i % 2 === 1) out.push(rect(x + gutter + i * colW, y, colW, h, { fill: t.zebra!, rx: 4 * s })); });

  for (let hh = startH; hh <= endH; hh++) {
    const yy = yOf(hh * 60);
    if (hh > startH && hh < endH) out.push(line(x + gutter - 4 * s, yy, x + w - (t.frame ? 0 : 0), yy, { stroke: t.rule, "stroke-width": t.gridLine * s }));
    if (t.halfHours && hh < endH) out.push(line(x + gutter, yy + hourH / 2, x + w, yy + hourH / 2, { stroke: t.rule, "stroke-width": t.gridLine * s * 0.8, "stroke-dasharray": `${n(1 * s)} ${n(2.5 * s)}` }));
    if (hh < endH) out.push(txt(R, x + gutter - 5 * s, yy + hs + 2.5 * s, hourLabel(hh, o.clock), hourFont, hs, t.ink3, { anchor: "end" }));
  }
  days.forEach((d, i) => {
    const cx = x + gutter + i * colW;
    out.push(line(cx, t.frame ? y : top, cx, y + h, { stroke: i === 0 ? t.rule : t.ruleSoft, "stroke-width": t.gridLine * s }));
    const full = L.days[DAYS.indexOf(d)], short = L.short[DAYS.indexOf(d)];
    const ds = (t.dayUpper ? 8 : 11) * s, tr = t.dayUpper ? 1.3 * s : 0;
    const label = (lb: string) => (t.dayUpper ? upper(lb) : lb);
    const pick = measure(label(full), t.fonts.day, ds, tr) <= colW - 10 * s ? label(full) : label(short);
    const base = y + headH / 2 + ds * 0.36;
    if (t.id === "blocks") out.push(txt(R, cx + 6 * s, base, pick, t.fonts.day, ds, t.ink));
    else out.push(txt(R, cx + colW / 2, base, pick, t.fonts.day, ds, t.dayUpper ? t.ink : t.ink2, { anchor: "middle", track: tr }));
  });
  if (t.headRule) out.push(line(x, top, x + w, top, { stroke: t.dark ? t.rule : t.ink, "stroke-width": t.headRule * s }));
  else out.push(line(x + gutter, top, x + w, top, { stroke: t.rule, "stroke-width": t.gridLine * s }));

  const lanes = laneLayout(slots);
  for (const sl of slots) {
    const di = days.indexOf(sl.day);
    if (di < 0) continue;
    const ln = lanes.get(sl.id) ?? { lane: 0, lanes: 1 };
    const lw = colW / ln.lanes;
    const ins = t.block.inset * s;
    const bx = x + gutter + di * colW + ln.lane * lw + ins + (ln.lane === 0 ? 0.5 * s : 0);
    const by = yOf(sl.start) + ins, bh = yOf(sl.end) - yOf(sl.start) - ins * 2;
    out.push(block(R, sl, bx, by, lw - ins * 2 - (ln.lane === 0 ? 0.5 * s : 0), bh));
  }
  if (!slots.length) out.push(txt(R, x + gutter + (w - gutter) / 2, top + (h - headH) / 2, L.empty, "displayItalic", 16 * s, t.ink3, { anchor: "middle" }));
  R.body.push(...out);
}

function block(R: Ctx, sl: Slot, x: number, y: number, w: number, h: number): string {
  const { t, o, s } = R;
  const b = t.block, c = blockColors(t, normalize(sl.color, "#555555"));
  const clash = !!o.clashIds?.has(sl.id);
  const out: string[] = [`<g${attrs({ "data-slot": sl.id, class: "slot" })}>`];
  const r = Math.min(b.radius * s, h / 2, w / 2);
  out.push(rect(x, y, w, h, { rx: r, fill: c.fill, stroke: c.stroke, "stroke-width": c.stroke ? b.stroke * s : undefined, "stroke-dasharray": sl.stale && c.stroke ? `${n(3 * s)} ${n(2 * s)}` : undefined }));
  if (c.bar && b.bar) {
    const bw = b.bar * s;
    out.push(sl.stale
      ? line(x + bw / 2, y, x + bw / 2, y + h, { stroke: c.bar, "stroke-width": bw, "stroke-dasharray": `${n(3 * s)} ${n(2 * s)}` })
      : path(r > 0.5 ? `M${P(x + r, y)}L${P(x + bw, y)}L${P(x + bw, y + h)}L${P(x + r, y + h)}Q${P(x, y + h, x, y + h - r)}L${P(x, y + r)}Q${P(x, y, x + r, y)}Z` : `M${P(x, y)}h${n(bw)}v${n(h)}h${n(-bw)}Z`, { fill: c.bar }));
  }
  if (clash) {
    const solid = b.fill === "solid";
    out.push(path(hatch(x + 1, y + 1, w - 2, h - 2, 5 * s), { stroke: solid ? "#ffffff" : t.hot, "stroke-width": 1.2 * s, "stroke-opacity": solid ? 0.28 : t.id === "mono" ? 0.22 : 0.32 }));
    out.push(rect(x, y, w, h, { rx: r, fill: "none", stroke: t.hot, "stroke-width": (solid ? 2.2 : 1.5) * s }));
  }
  const ear = sl.edited ? 6 * s : 0;
  if (sl.edited) out.push(dogEar(x + w, y, ear, c.mark));

  const bar = c.bar && b.bar ? b.bar * s : 0;
  const padX = (b.fill === "solid" || b.fill === "pastel" ? 6 : 4.5) * s, padY = (b.fill === "pastel" || b.fill === "solid" ? 5 : 4) * s;
  const cx = x + bar + padX, cw = w - bar - padX * 2;
  const avail = h - padY * 2;
  const ts0 = (b.fill === "solid" ? 11 : 10.5) * s;
  const longest = Math.max(...sl.title.split(/\s+/).map((wd) => measure(wd, t.fonts.blockTitle, ts0)));
  const ts = longest > cw ? Math.max(ts0 * 0.78, (ts0 * cw) / longest) : ts0, tlh = ts * 1.17;
  const ms = 7.8 * s, mlh = ms * 1.38, bsz = 6.8 * s;
  const kind = kindLabel(sl.kind);
  const d = details(R, sl);
  const timeStr = range(sl.start, sl.end, o.clock);
  const bW = kind ? measure(kind, "monoBold", bsz) + bsz * 0.76 + 4 * s : 0;
  const trH = Math.max(ms, bsz * 1.42) + 3 * s;

  if (cw < 14 * s || h < ts * 1.05) { out.push("</g>"); return out.join(""); }

  if (avail < trH + tlh - 1 * s) {
    const base = y + h / 2 + ts * 0.35;
    let lx = cx;
    if (kind && cw > bW + 20 * s) { out.push(badge(R, lx, base - 0.5 * s, kind, bsz, c).svg); lx += bW; }
    const startStr = clock(sl.start, o.clock);
    const stW = measure(startStr, t.fonts.time, ms) + 5 * s;
    const showT = cw - (lx - cx) - stW > 40 * s;
    out.push(txt(R, lx, base, ellipsize(sl.title, t.fonts.blockTitle, ts, cw - (lx - cx) - (showT ? stW : 0) - ear), t.fonts.blockTitle, ts, c.title));
    if (showT) out.push(txt(R, cx + cw - ear, base, startStr, t.fonts.time, ms, c.sub, { anchor: "end" }));
    out.push("</g>");
    return out.join("");
  }

  let used = trH + tlh;
  const natural = wrap(sl.title, t.fonts.blockTitle, ts, cw, 4).length;
  let titleN = 1;
  const meta: { s: string; f: FontKey; fill: string }[] = [];
  const tryAdd = (hh: number) => (used + hh <= avail + 0.5 * s ? ((used += hh), true) : false);
  if (d.room && tryAdd(mlh)) meta.push({ s: d.room, f: t.fonts.meta, fill: c.text });
  while (titleN < Math.min(natural, 3) && tryAdd(tlh)) titleN++;
  const teacherAt = meta.length;
  if (d.teacher && tryAdd(mlh)) meta.splice(teacherAt, 0, { s: d.teacher, f: "body", fill: c.sub });
  if (d.extra && tryAdd(mlh)) meta.push({ s: d.extra, f: t.fonts.meta, fill: c.sub });
  if (natural > titleN && titleN < 4 && tryAdd(tlh)) titleN++;

  let yy = y + padY + trH - 3 * s - (trH - 3 * s - ms * 0.74) / 2;
  let tx = cx;
  if (kind && cw > bW + 10 * s) { out.push(badge(R, tx, yy, kind, bsz, c).svg); tx += bW; }
  const tw = cw - (tx - cx) - ear;
  const timeFit = measure(timeStr, t.fonts.time, ms) <= tw ? timeStr : clock(sl.start, o.clock);
  if (measure(timeFit, t.fonts.time, ms) <= tw) out.push(txt(R, tx, yy, timeFit, t.fonts.time, ms, c.sub));
  yy = y + padY + trH + ts * 0.86;
  for (const l of wrap(sl.title, t.fonts.blockTitle, ts, cw, titleN)) { out.push(txt(R, cx, yy, l, t.fonts.blockTitle, ts, c.title)); yy += tlh; }
  yy += mlh - tlh + 1 * s;
  for (const m of meta) { out.push(txt(R, cx, yy, ellipsize(m.s, m.f, m.f === "body" ? ms * 1.08 : ms, cw), m.f, m.f === "body" ? ms * 1.08 : ms, m.fill)); yy += mlh; }
  out.push("</g>");
  return out.join("");
}

/* ---------------------------------------------------------------- agenda */

interface Row { h: number; draw: (x: number, y: number) => string }

function agendaRow(R: Ctx, sl: Slot, w: number, u: number, metaLines: number): Row {
  const { t, o } = R;
  const c = blockColors(t, normalize(sl.color, "#555555"));
  const filled = !(t.id === "compact" || t.block.fill === "outline");
  const inline = w / u < 230;
  const padX = (filled ? (inline ? 8 : 10) : 2) * u, padY = (filled ? (inline ? 6.5 : 8) : 7) * u;
  const ts = 11 * u, tlh = ts * 1.2, ms = 8 * u, mlh = ms * 1.42, tms = 9 * u, bsz = 7 * u;
  const timeW = inline ? 0 : measure(o.clock === 12 ? "12:00 PM" : "00:00", t.fonts.time, tms) + 12 * u;
  const lead = filled ? (t.block.bar ? t.block.bar * u : 0) : 14 * u;
  const cx0 = padX + lead + timeW, cw = w - cx0 - padX - (sl.edited ? 6 * u : 0);
  const kind = kindLabel(sl.kind);
  const bW = kind ? measure(kind, "monoBold", bsz) + bsz * 0.76 + 5 * u : 0;
  const tl = wrap(sl.title, t.fonts.blockTitle, ts, cw, 2, cw - bW);
  const d = details(R, sl);
  const metaStr = [d.room, d.teacher, d.extra].filter(Boolean).join(" · ");
  const timeStr = range(sl.start, sl.end, o.clock);
  const itw = inline ? measure(timeStr, "monoBold", ms) + measure(" · ", "monoBold", ms) + 2 * u : 0;
  const ml = inline
    ? metaLines ? wrap(metaStr, t.fonts.meta, ms, cw, metaLines, cw - itw) : []
    : metaLines ? wrap(metaStr, t.fonts.meta, ms, cw, metaLines) : [];
  const metaN = inline ? Math.max(1, ml.length) : ml.length;
  const contentH = tl.length * tlh + (metaN ? 2 * u + metaN * mlh : 0);
  const h = Math.max(contentH, inline ? 0 : tms * 2.5) + padY * 2;
  return {
    h,
    draw: (x, y) => {
      const out: string[] = [`<g${attrs({ "data-slot": sl.id, class: "slot" })}>`];
      const clash = !!o.clashIds?.has(sl.id);
      const r = t.block.radius * u;
      if (filled) {
        out.push(rect(x, y, w, h, { rx: r, fill: c.fill }));
        if (c.bar && t.block.bar) out.push(rect(x, y, t.block.bar * u, h, { fill: c.bar, rx: Math.min(r, 1.5 * u) }));
      } else if (t.id === "compact") out.push(`<circle${attrs({ cx: x + padX + 4 * u, cy: y + padY + ts * 0.5, r: 4 * u, fill: normalize(sl.color, t.ink) })}/>`);
      else out.push(rect(x + padX, y + padY, 3 * u, h - padY * 2, { fill: t.ink }));
      if (clash) {
        out.push(path(hatch(x + 1, y + 1, w - 2, h - 2, 6 * u), { stroke: t.block.fill === "solid" ? "#ffffff" : t.hot, "stroke-width": 1.1 * u, "stroke-opacity": t.block.fill === "solid" ? 0.22 : 0.16 }));
        out.push(rect(x, y, w, h, { rx: r, fill: "none", stroke: t.hot, "stroke-width": 1.5 * u }));
      }
      if (sl.edited) out.push(dogEar(x + w, y, 7 * u, filled ? c.mark : t.ink2));
      const tcol = filled ? c.title : t.ink, scol = filled ? c.sub : t.ink3;
      if (!inline) {
        const tx = x + padX + lead;
        out.push(txt(R, tx, y + padY + tms * 0.95, clock(sl.start, o.clock), t.fonts.time === "mono" ? "monoBold" : t.fonts.time, tms, tcol));
        out.push(txt(R, tx, y + padY + tms * 2.25, clock(sl.end, o.clock), "mono", tms * 0.92, scol));
      }
      let yy = y + padY + ts * 0.86;
      const cx = x + cx0;
      let lx = cx;
      if (kind) { out.push(badge(R, lx, yy - 0.5 * u, kind, bsz, c).svg); lx += bW; }
      tl.forEach((l, i) => { out.push(txt(R, i === 0 ? lx : cx, yy, l, t.fonts.blockTitle, ts, tcol)); yy += tlh; });
      yy += 2 * u - tlh + mlh - ms * 0.25;
      if (inline) {
        out.push(txt(R, cx, yy, timeStr + (ml.length ? " ·" : ""), "monoBold", ms, tcol));
        if (ml[0]) out.push(txt(R, cx + itw, yy, ml[0], t.fonts.meta, ms, scol));
        yy += mlh;
        for (const l of ml.slice(1)) { out.push(txt(R, cx, yy, l, t.fonts.meta, ms, scol)); yy += mlh; }
      } else for (const l of ml) { out.push(txt(R, cx, yy, l, t.fonts.meta, ms, scol)); yy += mlh; }
      out.push("</g>");
      return out.join("");
    },
  };
}

interface Section { h: number; draw: (x: number, y: number) => string }

function agendaSections(R: Ctx, slots: Slot[], w: number, u: number, metaLines: number): Section[] {
  const { t, L, o } = R;
  const filled = !(t.id === "compact" || t.block.fill === "outline");
  const gap = filled ? 4 * u : 0;
  return visibleDays(slots).map((d) => {
    const ds = slots.filter((sl) => sl.day === d).sort((a, b) => a.start - b.start || a.end - b.end);
    const rows = ds.map((sl) => agendaRow(R, sl, w, u, metaLines));
    const hs = (t.fonts.agendaDay.startsWith("display") ? 17 : 12.5) * u;
    const headH = hs * 1.35 + 6 * u;
    const h = headH + rows.reduce((a, r) => a + r.h + gap, 0) + 12 * u;
    return {
      h,
      draw: (x, y) => {
        const out: string[] = [];
        const name = L.days[DAYS.indexOf(d)];
        const hours = ds.reduce((a, sl) => a + (sl.end - sl.start) / 60, 0);
        const label = t.dayUpper && !t.fonts.agendaDay.startsWith("display") ? upper(name) : name;
        out.push(txt(R, x, y + hs, label, t.fonts.agendaDay, hs, t.ink, { track: t.fonts.agendaDay.startsWith("display") ? 0 : 0.6 * u }));
        const sum = ds.length ? `${ds.length} · ${num(hours, o.lang)} h` : L.free;
        out.push(txt(R, x + w, y + hs, sum, "mono", 7.5 * u, t.ink3, { anchor: "end", track: 0.4 * u }));
        out.push(line(x, y + headH - 3 * u, x + w, y + headH - 3 * u, { stroke: t.id === "mono" ? t.ink : t.rule, "stroke-width": (t.id === "mono" ? 1.2 : 0.8) * u }));
        let cy = y + headH + (filled ? 2 * u : 0);
        rows.forEach((r, i) => {
          out.push(r.draw(x, cy));
          cy += r.h + gap;
          if (!filled && i < rows.length - 1) out.push(line(x, cy, x + w, cy, { stroke: t.ruleSoft, "stroke-width": 0.7 * u }));
        });
        return out.join("");
      },
    };
  });
}

function partition(hs: number[], k: number): number[][] {
  let best: number[][] = [hs.map((_, i) => i)], bestMax = Infinity;
  const rec = (start: number, left: number, acc: number[][]) => {
    if (left === 1) {
      const all = [...acc, hs.map((_, i) => i).slice(start)];
      const mx = Math.max(...all.map((g) => g.reduce((a, i) => a + hs[i], 0)));
      if (mx < bestMax) { bestMax = mx; best = all; }
      return;
    }
    for (let e = start + 1; e <= hs.length - left + 1; e++) rec(e, left - 1, [...acc, hs.map((_, i) => i).slice(start, e)]);
  };
  rec(0, Math.max(1, Math.min(k, hs.length)), []);
  return best;
}

type Plan = [scale: number, metaLines: number, cols: number];

function plansFor(ks: number[], colsFirst: boolean): Plan[] {
  const plans: [number, number][] = [];
  for (const sc of [1.3, 1.22, 1.15, 1.07, 1, 0.94, 0.88, 0.82, 0.76]) plans.push([sc, 2]);
  for (const sc of [0.88, 0.82, 0.76, 0.7, 0.64]) plans.push([sc, 1]);
  for (const sc of [0.64, 0.58, 0.52, 0.46]) plans.push([sc, 0]);
  if (!colsFirst) return plans.flatMap(([sc, ml]) => ks.map((k) => [sc, ml, k] as Plan));
  const good = (k: number) => plans.filter(([sc, ml]) => ml > 0 && sc >= 0.76).map(([sc, ml]) => [sc, ml, k] as Plan);
  return [...ks.flatMap(good), ...plans.flatMap(([sc, ml]) => ks.map((k) => [sc, ml, k] as Plan))];
}

function agenda(R: Ctx, slots: Slot[], x: number, y: number, w: number, h: number, base: number, ks: number[], center: boolean, colsFirst = false) {
  const gapX = 22 * base;
  let chosen: { secs: Section[]; cols: number[][]; colW: number; u: number } | null = null;
  outer: for (const [sc, ml, k] of plansFor(ks, colsFirst)) {
    {
      const colW = (w - gapX * (k - 1)) / k, u = base * sc;
      const secs = agendaSections(R, slots, colW, u, ml);
      const cols = partition(secs.map((s) => s.h), k);
      const mx = Math.max(...cols.map((g) => g.reduce((a, i) => a + secs[i].h, 0)));
      if (mx <= h) { chosen = { secs, cols, colW, u }; break outer; }
      chosen = { secs, cols, colW, u };
    }
  }
  const { secs, cols, colW } = chosen!;
  const heights = cols.map((g) => g.reduce((a, i) => a + secs[i].h, 0));
  const offY = center ? Math.max(0, (h - Math.max(...heights)) / 2) : 0;
  cols.forEach((g, ci) => {
    let cy = y + offY;
    for (const i of g) { R.body.push(secs[i].draw(x + ci * (colW + gapX), cy)); cy += secs[i].h; }
  });
  if (!slots.length) R.body.push(txt(R, x + w / 2, y + h / 2, R.L.empty, "displayItalic", 16 * base, R.t.ink3, { anchor: "middle" }));
}

/* ---------------------------------------------------------------- entry */

export function renderScheduleSvg(input: Slot[], opts: RenderOptions): string {
  const t = THEMES[opts.theme] ?? THEMES.ledger;
  const paper = PAPERS[opts.paper] ?? PAPERS["a4-landscape"];
  const { w: W, h: H } = paper;
  const slots = input.filter((sl) => DAYS.includes(sl.day) && Number.isFinite(sl.start) && Number.isFinite(sl.end) && sl.end > sl.start)
    .map((sl) => ({ ...sl, start: Math.max(0, sl.start), end: Math.min(24 * 60, sl.end) }));
  const L = STR[opts.lang] ?? STR.bs;
  const s = opts.paper === "a5-landscape" ? 0.86 : 1;
  const R: Ctx = { t, o: opts, L, used: new Set(), s, W, H, body: [] };

  if (opts.paper === "phone") phone(R, slots);
  else {
    const M = (opts.paper === "a5-landscape" ? 28 : 36) * s;
    const top = header(R, slots, M, M, W - 2 * M) + 14 * s;
    let bottom = footer(R, slots, M, W - 2 * M, H - M + 6 * s) - 8 * s;
    const lg = opts.legend?.length ? legendLayout(R, opts.legend, W - 2 * M, (bottom - top) * (opts.paper === "a4-portrait" ? 0.3 : 0.26)) : null;
    if (lg) { bottom -= lg.h; R.body.push(lg.draw(M, bottom)); bottom -= 16 * s; }
    const ks = opts.paper === "a4-portrait" ? [2, 1] : [3, 2];
    if (t.layout === "agenda") agenda(R, slots, M, top + 4 * s, W - 2 * M, bottom - top - 4 * s, s, ks, false);
    else grid(R, slots, M, top, W - 2 * M, bottom - top);
  }

  const fontSrc = opts.fonts;
  const css = fontSrc ? fontFaceCss(R.used, typeof fontSrc === "function" ? fontSrc : (f) => fontSrc[f.file]) : "";
  const style = `<style>${css ? css + "\n" : ""}text{font-kerning:normal;text-rendering:geometricPrecision}</style>`;
  return `<svg xmlns="http://www.w3.org/2000/svg"${attrs({ viewBox: `0 0 ${W} ${H}`, width: W, height: H, "xml:lang": opts.lang })}>` +
    `<title>${esc(opts.title || L.title)}</title>${style}` + rect(0, 0, W, H, { fill: t.paper }) + R.body.join("") + `</svg>`;
}

function phone(R: Ctx, slots: Slot[]) {
  const { t, o, L, W, H } = R;
  const u = 3.4, M = 72;
  const safeTop = Math.round(H * 0.25), safeBottom = H - 240;
  const out: string[] = [];
  const titleSize = 22 * u * t.titleScale;
  out.push(txt(R, M, safeTop + 8 * u, upper(L.brand), t.fonts.kicker, 7.5 * u, t.ink3, { track: 1.4 * u }));
  const tf = t.fonts.title;
  out.push(txt(R, M, safeTop + 10 * u + titleSize * 0.92, ellipsize(o.title || L.title, tf, titleSize, W - 2 * M), tf, titleSize, t.ink));
  let y = safeTop + 10 * u + titleSize * 0.92;
  if (o.subtitle) { y += 14 * u; out.push(txt(R, M, y, ellipsize(o.subtitle, "body", 10 * u, W - 2 * M), "body", 10 * u, t.ink2)); }
  const st = stats(slots);
  y += 13 * u;
  out.push(txt(R, M, y, upper(`${st.count} ${st.count === 1 ? L.session : L.sessions}  ·  ${num(st.hours, o.lang)} h`), "monoBold", 7.5 * u, t.ink3, { track: 1 * u }));
  y += 8 * u;
  out.push(t.headerRule === "double"
    ? line(M, y, W - M, y, { stroke: t.ink, "stroke-width": 1.4 * u }) + line(M, y + 3 * u, W - M, y + 3 * u, { stroke: t.ink, "stroke-width": 0.6 * u })
    : line(M, y, W - M, y, { stroke: t.headerRule === "none" ? t.rule : t.ink, "stroke-width": (t.headerRule === "thick" ? 2.5 : 1) * u }));
  y += 14 * u;
  R.body.push(...out);
  const footY = safeBottom;
  R.body.push(txt(R, W / 2, footY, o.footer || L.brand, "mono", 7 * u, t.ink3, { anchor: "middle", track: 0.6 * u }));
  agenda(R, slots, M, y, W - 2 * M, footY - 18 * u - y, u, [1, 2], false, true);
}
